import { clearRecentRecords } from '@client/components/_core/navigation/search/recent-records';
import { message } from '@client/components/_ui/feedback/message';
import { getConfig } from '@client/config/runtime';
import { queryClient, trpcClient } from '@client/trpc/client';
import { createLocalStoragePersister, isJwtExpired } from '@client/utils';
import { capture } from '@client/utils/analytics';
import { getAuth0Client } from '@client/utils/auth/auth0Client';
import { AuthError, handleAuthError } from '@client/utils/auth/authErrorHandler';
import { authLock } from '@client/utils/auth/authLock';
import { crossTabTokenSync } from '@client/utils/auth/crossTabTokenSync';
import { armLogoutRedirectFallback } from '@client/utils/auth/sessionExpiry';
import { identifyAnonymous } from '@client/utils/feature-flags';
import * as Sentry from '@sentry/react';
import { SYSTEM_ORG_IDS, USER_FLAGS } from '@shared/constants';
import { hasPermission } from '@shared/permissions/roles';
import { decodeJwtToken } from '@shared/utils/core/jwt';
import { generateHref } from '@shared/utils/core/navigation';
import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import type { AuthStore, AuthStoreOrg, AuthStoreUser, AuthTokenRequestOptions, BootstrapResult } from './types';

type Auth0CacheMode = NonNullable<AuthTokenRequestOptions['auth0CacheMode']>;

const storage = createLocalStoragePersister<AuthStore>();

function hasMinimumTokenLifetime(expiration: Date, minValidityMs = 0, now = Date.now()) {
    return expiration.getTime() - now > minValidityMs;
}

/** A missing token (auth0-spa-js returns `undefined` on a cache miss) fails like an expired one. */
function assertTokenMeetsRequestedValidity(
    token: string | undefined,
    options?: AuthTokenRequestOptions,
): asserts token is string {
    const claims = decodeJwtToken(token);
    const expiration = claims?.exp ? new Date(claims.exp * 1000) : undefined;

    if (!claims || !expiration || !hasMinimumTokenLifetime(expiration, options?.minValidityMs ?? 0)) {
        throw new AuthError('token_expired');
    }
}

/**
 * In-flight `getTokenSilently` calls, keyed by the Auth0 cache mode they were issued with.
 *
 * `cacheMode: 'off'` is a refresh-token exchange, and the refresh tokens rotate: two
 * concurrent exchanges can invalidate the whole token family and sign the user out. Boot
 * now has several callers racing for the same token — the pre-render warm-up, `RequireAuth`,
 * `useAccessTokenRefresher` and `useUserPermissionSync` — so the second one joins the first
 * instead of starting its own. Mirrors `TokenRefreshManager`'s `refreshPromise`, which keeps
 * a single flight for the same reason. Keyed by cache mode so a `cache-only`/`on` caller
 * (the redirect callback) can never adopt, or be adopted by, a forced renewal; each caller
 * still runs `assertTokenMeetsRequestedValidity` on the shared result.
 */
const inFlightTokenRequests = new Map<Auth0CacheMode, Promise<string | undefined>>();

function requestTokenSilently(cacheMode: Auth0CacheMode): Promise<string | undefined> {
    const inFlight = inFlightTokenRequests.get(cacheMode);
    if (inFlight) {
        return inFlight;
    }

    const request = getAuth0Client()
        .getTokenSilently({
            authorizationParams: {
                audience: getConfig('AUTH0_AUDIENCE', import.meta.env.VITE_AUTH0_AUDIENCE),
                scope: 'openid profile email offline_access',
            },
            cacheMode,
        })
        .finally(() => {
            inFlightTokenRequests.delete(cacheMode);
        });

    inFlightTokenRequests.set(cacheMode, request);
    return request;
}

export const useAuthStore = create<AuthStore>()(
    persist<AuthStore>(
        (set, get) => ({
            user: undefined,
            org: undefined,
            authTokenExpired: false,
            isLoggingOut: false,
            isRedirectingToLogin: false,

            async login(accessToken, options): Promise<BootstrapResult> {
                return authLock.acquire('login', async () => {
                    const { setAuthToken, loadUser, loadOrg, setAuthTokenExpired } = get();
                    setAuthTokenExpired(false);
                    const decoded = decodeJwtToken(accessToken);
                    if (!decoded) {
                        throw new Error('AuthStore: Claims are undefined.');
                    }

                    setAuthToken(accessToken);

                    // Call the server bootstrap to resolve identity → user/org. The JWT itself
                    // carries only identity now; the server runs ensureUser +
                    // email/role/payment checks + default-org resolution and returns the orgId
                    // the SPA should use as X-Curium-Org-Id from this point on.
                    // `requestedOrgId` honours admin-login intent and deep-link orgs so the
                    // bootstrap doesn't silently default to the user's regular org when the
                    // callback requested admin console or a specific org.
                    const result = (await trpcClient.auth.bootstrap.mutate(
                        options?.requestedOrgId !== undefined ? { requestedOrgId: options.requestedOrgId } : undefined,
                    )) as BootstrapResult;
                    if (!result.ok) {
                        return result;
                    }

                    // Set pendingOrgId BEFORE loadUser/loadOrg so the tRPC header link picks it up.
                    set({ pendingOrgId: result.orgId });
                    try {
                        const [user, org] = await Promise.all([
                            loadUser(result.userId, result.orgId),
                            loadOrg(result.orgId),
                        ]);
                        set({ user, org, pendingOrgId: undefined });
                    } catch (err) {
                        set({ pendingOrgId: undefined });
                        throw err;
                    }

                    return result;
                });
            },

            async changeOrg(newOrgId) {
                return authLock.acquire('changeOrg', async () => {
                    const { loadUser, loadOrg, accessToken, user: existingUser } = get();

                    if (!existingUser?.id) {
                        throw new Error('AuthStore: cannot changeOrg without an active user.');
                    }

                    // Clear cached queries before pendingOrgId flips so the new org's data fetches
                    // start against an empty cache, preventing brief renders of the previous org's
                    // data through any not-yet-cleared query.
                    queryClient.clear();

                    set({ pendingOrgId: newOrgId });
                    try {
                        const [user, org] = await Promise.all([loadUser(existingUser.id, newOrgId), loadOrg(newOrgId)]);
                        set({ user, org, pendingOrgId: undefined });

                        // Broadcast org change explicitly with the new orgId (not inferred from token).
                        if (accessToken) {
                            crossTabTokenSync.broadcastOrgChange(accessToken, newOrgId);
                        }
                    } catch (err) {
                        set({ pendingOrgId: undefined });
                        throw err;
                    }
                });
            },

            setPendingOrgId(orgId) {
                set({ pendingOrgId: orgId });
            },

            clearAuthToken() {
                console.log('[AuthStore] Clearing auth token');
                set({
                    accessToken: '',
                    authTokenExpiration: undefined,
                    authTokenExpired: false,
                    pendingOrgId: undefined,
                });
            },

            setAuthToken(token) {
                if (!token) {
                    throw new Error('AuthStore: Token is undefined.');
                }

                console.log('[AuthStore] Setting auth token - start');

                // Decode token BEFORE setting state to avoid race condition
                const tokenExpiration = decodeJwtToken(token)?.exp;
                const expirationDate = tokenExpiration ? new Date(tokenExpiration * 1000) : undefined;

                // ATOMIC UPDATE - all state changes in ONE set() call to prevent race conditions
                // Previously: 3 separate set() calls created a 10-20ms window where state was inconsistent
                // This caused tRPC requests to think the token was expired when authTokenExpiration was still undefined
                set({
                    accessToken: token,
                    authTokenExpiration: expirationDate,
                    authTokenExpired: false,
                });

                console.log('[AuthStore] Auth token set successfully', {
                    hasToken: true,
                    expiresAt: expirationDate?.toISOString(),
                    timestamp: new Date().toISOString(),
                });

                // Broadcast token update to other tabs
                crossTabTokenSync.broadcastTokenUpdate(token);
            },

            getAuthToken: () => get().accessToken,

            setAuthTokenExpired: (value) => {
                set({ authTokenExpired: value });
                if (value) {
                    // Broadcast token expiration to other tabs
                    crossTabTokenSync.broadcastTokenExpired();
                }
            },

            setUser(user) {
                set({ user });
            },

            async logout(reason = 'unknown') {
                try {
                    // Signal the rest of the app that a logout is underway. Background
                    // token-refresh machinery (TokenRefreshManager, permission sync,
                    // RequireAuth) stands down so an in-flight Auth0 renewal cannot surface a
                    // misleading "session could not be renewed" error toast while the Auth0
                    // session is being deliberately revoked.
                    set({ isLoggingOut: true });

                    // Clear session expired flag first to prevent modal from showing
                    const { setAuthTokenExpired, clearAuthToken } = get();
                    setAuthTokenExpired(false);

                    try {
                        capture('auth_logout', { reason });
                        identifyAnonymous();
                    } catch (e) {
                        console.log('Error destroying product fruits', e);
                    }

                    clearAuthToken();

                    // Palette recents store only record entity and ID references. Sweep those
                    // identifiers before the browser navigates away so they do not survive the
                    // session on a shared machine.
                    clearRecentRecords();

                    await getAuth0Client().logout({
                        logoutParams: {
                            returnTo: generateHref({
                                baseUrl: window.location.origin,
                                to: '/logoutin',
                            }),
                            //federated: true,
                        },
                    });

                    Sentry.setUser(null);

                    // Broadcast logout to other tabs
                    crossTabTokenSync.broadcastLogout();

                    // The Auth0 SDK has started the redirect, but a "Leave site?" prompt answered
                    // with Stay cancels it without an error. Bound the window so this tab cannot
                    // stay in logging-out mode, refusing every request, for good.
                    armLogoutRedirectFallback();
                } catch (e) {
                    console.error('Error logging out', e);
                    // Logout failed before the browser could navigate away — un-stick the
                    // flag so the app (and its token refresh) remains usable.
                    set({ isLoggingOut: false });
                } finally {
                    message.destroy();
                }
            },

            // Additional methods and computed properties
            getIsAuthenticated() {
                const { user, accessToken, isTokenExpired } = get();

                if (!user || !accessToken) {
                    return false;
                }

                return !isTokenExpired();
            },

            isTokenExpired() {
                const { accessToken, authTokenExpiration } = get();

                // If no token exists, it's not "expired" - it's absent
                if (!accessToken) {
                    return false;
                }

                if (!authTokenExpiration) {
                    return true;
                }

                const expired = isJwtExpired(authTokenExpiration.getTime() / 1000);

                if (expired) {
                    console.warn('[AuthStore] Token is expired', {
                        expiresAt: authTokenExpiration.toISOString(),
                        now: new Date().toISOString(),
                    });
                }

                return expired;
            },

            isTokenSet() {
                const { accessToken } = get();
                return !!accessToken;
            },

            hasValidToken(options?: AuthTokenRequestOptions) {
                const { accessToken, authTokenExpiration } = get();
                const minValidityMs = options?.minValidityMs ?? 0;

                if (!accessToken || !authTokenExpiration) {
                    return false;
                }

                if (
                    isJwtExpired(authTokenExpiration.getTime() / 1000) ||
                    !hasMinimumTokenLifetime(authTokenExpiration, minValidityMs)
                ) {
                    return false;
                }

                // Org validity is no longer encoded in the token — that check is now server-side
                // per request via X-Curium-Org-Id + resolveUserContext.
                return true;
            },

            async loadUser(userId: bigint, orgId: bigint) {
                const [dbUser, orgs] = await Promise.all([
                    trpcClient.user.getUser.query({
                        id: userId,
                        orgId,
                        include: ['handlingPartyIds', 'trainingManagerHandlingPartyIds'],
                    }),
                    trpcClient.user.getMyOrgs.query(),
                ]);

                const flagConditions = [
                    { condition: dbUser?.isPaymentAdmin, flag: USER_FLAGS.PAYMENT_ADMIN },
                    { condition: dbUser.canViewUnallocatedClaims, flag: USER_FLAGS.CAN_VIEW_UNALLOCATED_CLAIMS },
                ];

                return {
                    termsAccepted: dbUser.termsAccepted === true,
                    firstName: dbUser.firstName || '',
                    lastName: dbUser.lastName || '',
                    photoUrl: dbUser.photoUrl || '',
                    signedUpAt: dbUser.createdAt,
                    handlingPartyIds: dbUser.handlingPartyIds || [],
                    trainingManagerHandlingPartyIds: dbUser.trainingManagerHandlingPartyIds || [],
                    isEmployee: dbUser.isEmployee || false,
                    position: dbUser.position || '',
                    isSupportAccount: dbUser.isSupportAccount || false,
                    canViewUnallocatedClaims: dbUser.canViewUnallocatedClaims || false,
                    canCreatePayment: false,
                    canApprovePayment: false,
                    orgId: dbUser.orgId,
                    id: dbUser.userId,
                    auth0Id: dbUser.auth0Id,
                    email: dbUser.email,
                    phone: dbUser.phone,
                    roles: dbUser.roles,
                    fullName: dbUser.fullName,
                    flags: flagConditions.filter(({ condition }) => condition).map(({ flag }) => flag),
                    userOrgs: orgs.map((el) => ({ id: el.id, name: el.name })),
                    provider: dbUser.provider,
                    isSsoActivated: dbUser.isSsoActivated,
                    allowMigrationToSso: dbUser.allowMigrationToSso,
                };
            },

            async loadOrg(orgId: bigint) {
                if (orgId === SYSTEM_ORG_IDS.SYSTEM_CONSOLE) {
                    return {
                        id: SYSTEM_ORG_IDS.SYSTEM_CONSOLE,
                        name: '',
                        key: '',
                        enabledModules: [],
                        isLiteOrg: false,
                    } satisfies AuthStoreOrg;
                }

                const org = await trpcClient.org.getOrg.query({ orgId });

                if (!org) {
                    throw new Error("AuthStore: Can't load org details.");
                }

                return {
                    id: org.id,
                    name: org.name,
                    key: org.key,
                    enabledModules: org.enabledModules || [],
                    isLiteOrg: org.isLiteOrg,
                } satisfies AuthStoreOrg;
            },

            async reloadUser(accessToken?: string) {
                return authLock.acquire('reloadUser', async () => {
                    const { setAuthToken, loadUser, setUser, user: existingUser } = get();

                    const token = accessToken || get().accessToken;
                    if (!token) {
                        throw new Error('AuthStore: Token is undefined.');
                    }
                    if (!existingUser?.id || !existingUser?.orgId) {
                        throw new Error('AuthStore: cannot reloadUser without an active user/org.');
                    }

                    if (accessToken) {
                        setAuthToken(accessToken);
                    }

                    const user = await loadUser(existingUser.id, existingUser.orgId);
                    setUser(user);
                });
            },

            async getAccessToken(options?: AuthTokenRequestOptions) {
                const { hasValidToken, accessToken, isLoggingOut } = get();

                // A logout is underway — the Auth0 session is being revoked and the browser
                // is about to navigate away. A renewal here would fail with
                // MissingRefreshTokenError and surface a misleading error; bail out quietly
                // and let the logout flow own the redirect.
                if (isLoggingOut) {
                    throw new AuthError('login_required', 'getAccessToken skipped: logout in progress');
                }

                try {
                    if (hasValidToken(options)) {
                        return accessToken!;
                    }

                    const token = await requestTokenSilently(options?.auth0CacheMode ?? 'off');
                    assertTokenMeetsRequestedValidity(token, options);
                    return token;
                } catch (e) {
                    // Background refreshes (`silent`) handle failures via their own retry /
                    // session-expiry flow, so they must not pop an error toast or trigger a
                    // competing redirect. Interactive callers keep the toast + redirect.
                    const authError = handleAuthError(e, {
                        showMessage: !options?.silent,
                        redirectToLogin: !options?.silent,
                        context: 'getAccessToken',
                    });
                    throw authError;
                }
            },

            allowMigrationToSso() {
                const { setUser, user } = get();

                if (!user) {
                    throw new Error('AuthStore: User is undefined.');
                }

                setUser({ ...user, allowMigrationToSso: true });
            },
        }),
        {
            name: 'auth',
            storage: storage,
            // pendingOrgId is transient — it shadows `org.id` only during the brief window
            // inside login()/changeOrg() while loadUser/loadOrg run. Persisting it across reload
            // means a crash mid-transition would leave stale orgId on every future request.
            // isLoggingOut and isRedirectingToLogin are likewise transient: persisting either would
            // leave a stuck `true` on the next session and permanently break token renewal or
            // refuse every signed-in request.
            partialize: (state) =>
                Object.fromEntries(
                    Object.entries(state).filter(
                        ([key]) =>
                            !['allowMigrationToSso', 'pendingOrgId', 'isLoggingOut', 'isRedirectingToLogin'].includes(
                                key,
                            ),
                    ),
                ) as AuthStore,
        },
    ),
);

// Stable fallbacks: zustand 5's `create` hook compares snapshots with Object.is, so a
// fresh `[]` per read re-renders until React throws "Maximum update depth exceeded".
const EMPTY_USER_ROLES: AuthStoreUser['roles'] = [];
const EMPTY_USER_FLAGS: AuthStoreUser['flags'] = [];
const EMPTY_USER_ORGS: AuthStoreUser['userOrgs'] = [];
const EMPTY_ENABLED_MODULES: AuthStoreOrg['enabledModules'] = [];

// Selector functions
export const selectUser = (state: AuthStore) => state.user;
export const selectOrg = (state: AuthStore) => state.org;
export const selectAccessToken = (state: AuthStore) => state.accessToken;
export const selectAuthTokenExpiration = (state: AuthStore) => state.authTokenExpiration;
export const selectAuthTokenExpired = (state: AuthStore) => state.authTokenExpired;

// Computed selectors
export const selectIsAuthenticated = (state: AuthStore): boolean => {
    if (!state.user || !state.accessToken) {
        return false;
    }
    return !selectIsTokenExpired(state);
};

export const selectIsTokenExpired = (state: AuthStore): boolean => {
    const { accessToken, authTokenExpiration } = state;

    // If no token exists, it's not "expired" - it's absent
    if (!accessToken) {
        return false;
    }

    if (!authTokenExpiration) {
        return true;
    }

    return isJwtExpired(authTokenExpiration.getTime() / 1000);
};

export const selectIsAdmin = (state: AuthStore): boolean => {
    const user = state.user;
    if (!user) return false;
    return hasPermission(user.roles, 'org:admin') || hasPermission(user.roles, 'super:admin');
};

export const selectIsSuperAdmin = (state: AuthStore): boolean => {
    return hasPermission(state.user?.roles ?? [], 'super:admin');
};

export const selectIsOrgAdmin = (state: AuthStore): boolean => {
    return hasPermission(state.user?.roles ?? [], 'org:admin');
};

export const selectHasOrgAccess = (state: AuthStore): boolean => {
    const { user, org } = state;
    if (!user || !org) return false;

    // Super admins have access to all orgs
    if (hasPermission(user.roles, 'super:admin')) return true;

    // Check if user's current org matches
    return user.orgId === org.id;
};

export const selectUserRoles = (state: AuthStore) => state.user?.roles ?? EMPTY_USER_ROLES;
export const selectUserFlags = (state: AuthStore) => state.user?.flags ?? EMPTY_USER_FLAGS;
export const selectUserOrgs = (state: AuthStore) => state.user?.userOrgs ?? EMPTY_USER_ORGS;
export const selectEnabledModules = (state: AuthStore) => state.org?.enabledModules ?? EMPTY_ENABLED_MODULES;
export const selectIsLiteOrg = (state: AuthStore) => state.org?.isLiteOrg ?? false;

// Session selectors
export const selectIsSessionValid = (state: AuthStore): boolean => {
    return selectIsAuthenticated(state) && !selectIsTokenExpired(state);
};

export const selectNeedsTermsAcceptance = (state: AuthStore): boolean => {
    return state.user ? !state.user.termsAccepted : false;
};

export const selectIsSSOUser = (state: AuthStore): boolean => {
    return state.user?.isSsoActivated ?? false;
};

export const selectCanMigrateToSSO = (state: AuthStore): boolean => {
    return state.user?.allowMigrationToSso ?? false;
};
